import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmDialog from '../components/reusable/confirm-dialog/ConfirmDialog';
import { useRegisterDismissLayer } from '../context/BackLayerStackContext';

interface IUseUnsavedChangesGuardOptions {
  isDirty: boolean;
  leaveAction: () => void;
}

function resolveLeaveAction(
  action: unknown,
  fallbackAction: () => void,
): () => void {
  return typeof action === 'function' ? (action as () => void) : fallbackAction;
}

export function useUnsavedChangesGuard(
  options: IUseUnsavedChangesGuardOptions,
) {
  const { isDirty, leaveAction } = options;
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const nextLeaveActionRef = useRef<(() => void) | null>(null);
  const skipGuardOnceRef = useRef(false);

  const runLeave = useCallback(
    (action?: unknown) => {
      const resolvedAction = resolveLeaveAction(action, leaveAction);
      if (skipGuardOnceRef.current || !isDirty) {
        skipGuardOnceRef.current = false;
        resolvedAction();
        return;
      }
      nextLeaveActionRef.current = resolvedAction;
      setIsConfirmOpen(true);
    },
    [isDirty, leaveAction],
  );

  const allowAndLeave = useCallback(
    (action?: unknown) => {
      skipGuardOnceRef.current = true;
      resolveLeaveAction(action, leaveAction)();
    },
    [leaveAction],
  );

  const onConfirmLeave = useCallback(() => {
    setIsConfirmOpen(false);
    const action = nextLeaveActionRef.current;
    nextLeaveActionRef.current = null;
    skipGuardOnceRef.current = true;
    (action || leaveAction)();
  }, [leaveAction]);

  const onRejectLeave = useCallback(() => {
    setIsConfirmOpen(false);
    nextLeaveActionRef.current = null;
  }, []);

  useRegisterDismissLayer(isDirty, (pendingAction) => runLeave(pendingAction));

  const dialog = (
    <ConfirmDialog
      open={isConfirmOpen}
      onClose={onRejectLeave}
      onSubmit={onConfirmLeave}
      title={t('common.unsavedChangesTitle')}>
      {t('common.unsavedChangesDescription')}
    </ConfirmDialog>
  );

  return {
    runLeave,
    allowAndLeave,
    dialog,
  };
}
